<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <SearchPatientByExternalIdView @prepopulate-order-info="handlePrepopulateOrderInfo" />
        <div class="detail-page__back" @click="router.back()">
          <v-icon size="18">mdi-arrow-left</v-icon>
          <span>Back to Submissions</span>
        </div>
        <h1 ref="formHeadingRef" class="text-primary weight-600 pb-4">Create New Submission</h1>
        <v-row>
          <v-col cols="12" class="submission-detail-section">
            <v-container class="patient-information-form-wrapper">
              <div v-if="preloader">
                <SubmissionPageSkeleton />
              </div>
              <!-- The page couldn't be set up at all (e.g. account settings failed to load) —
                   show only the error, never a form driven by settings we don't have. -->
              <div v-else-if="pageLoadFailed" class="mt-4">
                <v-alert type="error">
                  <div v-for="error in errors" :key="error">{{ error }}</div>
                </v-alert>
              </div>
              <div v-else>
                <!-- Error display -->
                <div v-if="errors.length > 0" class="mt-4">
                  <v-alert type="error">
                    <div v-for="error in errors" :key="error">{{ error }}</div>
                  </v-alert>
                </div>
                <v-row>
                  <v-col cols="12" class="submission-detail-section">
                    <AutofillFromDocumentsComponent
                      v-if="autofillEnabled"
                      ref="autofillRef"
                      :supporting-docs-count="supportingDocsCount"
                      :is-running="autofillInProgress"
                      :status="autofillStatus"
                      :error="autofillError"
                      :candidate-count="autofillCandidateCount"
                      :candidates="autofillCandidates"
                      :diagnoses="autofillDiagnoses"
                      :restored-docs="autofillRestoredDocs"
                      :attached-links="attachedDocumentLinks"
                      :order-info="orderInfo"
                      @attach="attachSupplementalDoc"
                      @detach="detachSupplementalDoc"
                      @run="handleRunAutofill"
                      @apply="handleApplyAutofillValue"
                      @apply-diagnosis="handleApplyAutofillDiagnosis" />

                    <v-expansion-panels v-if="!preloader" v-model="expandedSections" multiple>
                      <!-- Patient Information Section -->
                      <v-expansion-panel value="patient-info">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-account" class="mr-2"></v-icon>
                          Patient Information
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <PatientInformationFormPreview
                            :order-info="orderInfo"
                            :is-editable="true"
                            :form-button-clicked="formButtonClicked"
                            @update-order-info-field="updateOrderInfoField" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Additional Patient Charts (multi-patient chart support) -->
                      <v-alert
                        v-if="
                          orderInfo.additionalPatientExternalIds && orderInfo.additionalPatientExternalIds.length > 0
                        "
                        type="info"
                        variant="tonal"
                        class="mb-4">
                        <div class="text-subtitle-2 mb-1">Additional Patient Charts</div>
                        <div class="d-flex flex-wrap ga-2">
                          <v-chip
                            v-for="patientChart in orderInfo.additionalPatientExternalIds"
                            :key="patientChart.externalId"
                            size="small"
                            variant="outlined">
                            {{ patientChart.firstName }} {{ patientChart.lastName }} — MRN:
                            {{ patientChart.externalId }}
                          </v-chip>
                        </div>
                      </v-alert>

                      <!-- Provider Information Section -->
                      <v-expansion-panel value="provider-info">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-doctor" class="mr-2"></v-icon>
                          Provider Information
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <ProviderInformationFormPreview
                            :order-info="orderInfo"
                            :is-editable="true"
                            :form-button-clicked="formButtonClicked"
                            @update-order-info-field="updateOrderInfoField" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Medical Information Section -->
                      <v-expansion-panel value="medical-info">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-medication" class="mr-2"></v-icon>
                          Prescription Information
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <MedicalInformationForm
                            :order-info="orderInfo"
                            :is-editable="true"
                            :form-button-clicked="formButtonClicked"
                            :allow-additional-medications="true"
                            @update-order-info-field="updateOrderInfoField"
                            @add-extra-medication="addExtraMedication"
                            @remove-extra-medication="removeExtraMedication"
                            @remove-primary-medication="removePrimaryMedication"
                            @update-extra-medication="updateExtraMedicationField"
                            @add-additional-medication="addAdditionalMedication"
                            @remove-additional-medication="removeAdditionalMedication"
                            @update-additional-medication-field="updateAdditionalMedicationField" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Patient Diagnosis Information Section -->
                      <v-expansion-panel value="diagnosis-info">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-account-heart" class="mr-2"></v-icon>
                          Patient Diagnosis Information
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <v-row class="patient-information-container">
                            <v-col cols="12">
                              <v-row class="patient-information">
                                <v-col cols="12" class="header-with-select-box">
                                  <DiagnosisSelectionPreviewComponent
                                    :medication="orderInfo.medication"
                                    :order-key="orderInfo.orderKey"
                                    :primary-diagnosis-name="orderInfo.primaryDiagnosisName"
                                    :primary-diagnosis-icd10-code="orderInfo.primaryDiagnosisIcd10Code"
                                    :secondary-diagnosis="orderInfo.secondaryDiagnosis"
                                    :is-editable="true"
                                    :form-button-clicked="formButtonClicked"
                                    @update-primary-diagnosis="updatePrimaryDiagnosis"
                                    @update-other-diagnosis="updateOtherDiagnosis"
                                    @add-other-diagnosis="addOtherDiagnosis" />
                                </v-col>
                              </v-row>
                            </v-col>
                          </v-row>
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Supporting Documents Section -->
                      <v-expansion-panel value="supporting-docs">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-file-document" class="mr-2"></v-icon>
                          Supporting Documents
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <SupportingDocumentsPreviewComponent
                            :order-key="orderInfo.orderKey"
                            :medical-necessity="orderInfo.medicalNecessity"
                            :medical-necessity-letter="orderInfo.medicalNecessityLetter"
                            :benefit-type="orderInfo.benefitType"
                            :patient-chart="orderInfo.patientChart"
                            :medical-necessity-template="orderInfo.medicalNecessityTemplate"
                            :supplemental-docs="supplementalDocs"
                            :form-button-clicked="formButtonClicked"
                            :is-editable="true"
                            :cmm-budgeted="
                              orderInfo.submissionMethod === 'CoverMyMeds' && !orderInfo.faxSubmissionFormDetail
                            "
                            @add-uploaded-file="updateSupportingDocs"
                            @delete-uploaded-file="handleDeleteFile"
                            @add-free-form-supplemental-docs="addFreeFormSupplementalDocs" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Expedited Review Section -->
                      <v-expansion-panel value="expedited-review">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-clock-fast" class="mr-2"></v-icon>
                          Expedited Review
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <ExpeditedReviewComponent
                            :order-key="orderInfo.orderKey"
                            :expedited-review-within24h="orderInfo.expeditedReviewWithin24h"
                            :expedited-review-statement="orderInfo.expeditedReviewStatement"
                            :is-editable="true"
                            :form-button-clicked="formButtonClicked"
                            @update-order-info-field="updateOrderInfoField" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>

                      <!-- Fax Form Selection Section (LAM-5197) — only for clients with mapped fax forms -->
                      <v-expansion-panel v-if="faxForms.length > 0" value="fax-form">
                        <v-expansion-panel-title class="text-primary weight-600">
                          <v-icon icon="mdi-fax" class="mr-2"></v-icon>
                          Fax Submission
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                          <FaxFormSelectionComponent
                            :fax-forms="faxForms"
                            :fax-submission-form-detail="orderInfo.faxSubmissionFormDetail"
                            :is-editable="true"
                            @update:opted-in="faxFormOptedIn = $event"
                            @update-order-info-field="updateOrderInfoField" />
                        </v-expansion-panel-text>
                      </v-expansion-panel>
                    </v-expansion-panels>

                    <!-- Validation status -->
                    <v-card class="mb-4 mt-4">
                      <v-card-title>Validation Status</v-card-title>
                      <v-card-text>
                        <v-chip :color="validPatientInfo() ? 'success' : 'error'" class="mr-2">
                          Patient Info: {{ validPatientInfo() ? "Valid" : "Invalid" }}
                        </v-chip>
                        <v-chip :color="validProviderInfo() ? 'success' : 'error'" class="mr-2">
                          Provider Info: {{ validProviderInfo() ? "Valid" : "Invalid" }}
                        </v-chip>
                        <v-chip :color="validMedicationInfo() ? 'success' : 'error'" class="mr-2">
                          Medication Info: {{ validMedicationInfo() ? "Valid" : "Invalid" }}
                        </v-chip>
                        <v-chip :color="validDiagnosisInfo() ? 'success' : 'error'" class="mr-2">
                          Diagnosis Info: {{ validDiagnosisInfo() ? "Valid" : "Invalid" }}
                        </v-chip>
                      </v-card-text>
                    </v-card>

                    <!-- Action buttons for new submission creation -->
                    <v-row class="mt-4">
                      <v-col cols="auto">
                        <v-btn
                          variant="outlined"
                          :disabled="draftSavingInProgress"
                          :loading="draftSavingInProgress"
                          @click="handleSaveDraft">
                          Save Draft
                        </v-btn>
                      </v-col>
                      <v-col cols="auto">
                        <v-btn :disabled="draftSavingInProgress" @click="handleCreateSubmission">
                          Create Submission
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </div>
            </v-container>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-if="route.params?.id">
      <v-col cols="12">
        <br />
        <AuditLogList :order-info-id="route.params?.id" />
      </v-col>
    </v-row>
    <v-dialog v-model="showCreateSuccessDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h6" color="green">
          {{ batchSubmissionResults ? "Submissions Created" : "Submission Created" }}
        </v-card-title>
        <v-card-text>
          <template v-if="batchSubmissionResults">
            <div>
              {{ batchSubmissionResults.filter((result) => result.success).length }} of
              {{ batchSubmissionResults.length }} medications submitted successfully. Fetching forms and questions from
              external portals may take up to 15 minutes.
            </div>
            <v-list density="compact" class="mt-2">
              <v-list-item v-for="(result, index) in batchSubmissionResults" :key="index">
                <template #prepend>
                  <v-icon
                    :color="result.success ? 'success' : 'error'"
                    :icon="result.success ? 'mdi-check-circle' : 'mdi-alert-circle'" />
                </template>
                <v-list-item-title>{{ result.medication }}</v-list-item-title>
                <v-list-item-subtitle v-if="!result.success">
                  {{ result.skippedDuplicate ? "Skipped — duplicate medication" : result.error }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </template>
          <template v-else>
            Your submission has been created successfully. Fetching form and questions from external portal may take up
            to 15 minutes. You may return to all submissions or create another submission.
          </template>
        </v-card-text>
        <v-card-actions class="pt-0">
          <v-spacer />
          <v-btn variant="outlined" @click="onSuccessReturnToAll">Return to All Submissions</v-btn>
          <v-btn variant="flat" color="primary" @click="onSuccessCreateAnother">Create Another Submission</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { computed, ref, onBeforeMount, onUnmounted, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useOrderInfoStore } from "@/stores/orderInfoStore";
import { useRequirementsStore } from "@/stores/requirementsStore";
import { useOrderInfoUpdates } from "@/composables/useOrderInfoUpdates";
import { tryParseApiErrors, clearOrderInfoStorage } from "@/utils";
import { convertStateToFullName } from "@/utils/usaStates";
import PatientInformationFormPreview from "@/pages/PatientInformationFormPreview.vue";
import ProviderInformationFormPreview from "@/pages/ProviderInformationFormPreview.vue";
import MedicalInformationForm from "@/pages/MedicalInformationForm.vue";
import DiagnosisSelectionPreviewComponent from "@/components/DiagnosisSelectionPreviewComponent.vue";
import SupportingDocumentsPreviewComponent from "@/components/SupportingDocumentsPreviewComponent.vue";
import SubmissionPageSkeleton from "@/components/SubmissionPageSkeleton.vue";
import AuditLogList from "@/components/AuditLogList.vue";
import SearchPatientByExternalIdView from "@/components/SearchPatientByExternalIdView.vue";
import AutofillFromDocumentsComponent from "@/components/AutofillFromDocumentsComponent.vue";
import {
  AUTOFILL_DIAGNOSIS_SLOT,
  AUTOFILL_POLL_INTERVAL_MS,
  AUTOFILL_POLL_MAX_CONSECUTIVE_FAILURES,
  AUTOFILL_POLL_TIMEOUT_MS,
  AUTOFILL_STATUS,
} from "@/constants/autofill";
import ExpeditedReviewComponent from "@/components/ExpeditedReviewComponent.vue";
import FaxFormSelectionComponent from "@/components/FaxFormSelectionComponent.vue";
import { orderInfoService } from "@/services/orderInfoService";
import { mainServices } from "@/services/mainServices";

const { orderInfo } = storeToRefs(useOrderInfoStore());
const requirementsStore = useRequirementsStore();
const route = useRoute();
const {
  supplementalDocs,
  formButtonClicked,
  updateOrderInfoField,
  addExtraMedication,
  removeExtraMedication,
  removePrimaryMedication,
  updateExtraMedicationField,
  addAdditionalMedication,
  removeAdditionalMedication,
  updateAdditionalMedicationField,
  addOtherDiagnosis,
  updatePrimaryDiagnosis,
  updateOtherDiagnosis,
  updateSupportingDocs,
  addFreeFormSupplementalDocs,
  attachSupplementalDoc,
  detachSupplementalDoc,
  validPatientInfo,
  validProviderInfo,
  validMedicationInfo,
  validAdditionalMedicationsInfo,
  validDiagnosisInfo,
  validSupportingDocs,
  savePartialOrderInfo,
  mergeSupplementalDocsWithPatientChart,
} = useOrderInfoUpdates(orderInfo);

function handleDeleteFile(downloadLinkInfo) {
  // Mark as delete and use existing updateSupportingDocs handler
  downloadLinkInfo.isDelete = true;
  updateSupportingDocs(downloadLinkInfo);
}

const router = useRouter();
const expandedSections = ref([]);
const draftSavingInProgress = ref(false);
const errors = ref([]);
// Opening an existing draft starts in the skeleton: the client-config fetch awaited before loadOrder()
// otherwise paints the form empty for its whole round-trip (LAM-5432).
const preloader = ref(!!route.params.id);
// Set when the page can't be set up at all, so the form is replaced by the error rather than
// rendered alongside it.
const pageLoadFailed = ref(false);
const showCreateSuccessDialog = ref(false);
// Per-medication outcomes when additional medications were queued (LAM-4841) — null for an
// ordinary single-medication submission, which keeps showing today's plain success message.
const batchSubmissionResults = ref(null);
const formHeadingRef = ref(null);
const autofillRef = ref(null);
// Per-tenant rollout flag, off by default (create_submission_autofill_enabled). The API refuses
// both kickoff and poll without it, so this only hides UI that could not work anyway.
const autofillEnabled = computed(
  () => requirementsStore.clientConfig?.priorAuth?.createSubmissionAutofillEnabled === true
);
// Fax forms mapped to this company (LAM-5197) — empty keeps the whole fax panel unrendered.
const faxForms = ref([]);
const faxFormOptedIn = ref(false);
const autofillInProgress = ref(false);
const autofillStatus = ref(AUTOFILL_STATUS.IDLE);
const autofillError = ref("");
// Per-field candidate values from the last autofill run, keyed by orderInfo field name.
// The first candidate of each field is written to the form as soon as the run lands
// (applyAutofillDefaults); the rest stay clickable alternatives.
const autofillCandidates = ref(null);
// Diagnosis candidates are separate: they are (name, icd10Code) pairs, and each can go into
// the primary slot or be appended to the secondary list.
const autofillDiagnoses = ref([]);
// Autofill-only uploads recovered from a previous run, so they survive a reload.
const autofillRestoredDocs = ref([]);
let autofillPollTimer = null;

// Documents already attached to the submission — the autofill job reads these by default.
// patientChart holds saved/merged docs, supplementalDocs holds ones added but not yet merged.
const attachedDocumentLinks = computed(() => {
  const collectLinks = (docs) => (Array.isArray(docs) ? docs : []).flatMap((doc) => doc?.links || []);
  return [...collectLinks(orderInfo.value.patientChart), ...collectLinks(supplementalDocs.value)];
});
const supportingDocsCount = computed(() => attachedDocumentLinks.value.length);

const autofillCandidateCount = computed(
  () => Object.keys(autofillCandidates.value || {}).length + (autofillDiagnoses.value.length > 0 ? 1 : 0)
);

// Put the draft's key in the URL once it exists. Reopening a draft by id is what drives
// loadOrder() -> restoreAutofillRun(), so without this a reload of the page the draft was created
// on takes the no-id path and loses the run (and the uploads behind it) that is still on the order.
function syncDraftKeyToUrl() {
  const orderKey = orderInfo.value.orderKey;
  if (!orderKey || route.params.id === orderKey) return;
  // Same route and component, so this only rewrites the URL — it does not re-run the page setup.
  router.replace({ name: "create-submission", params: { id: orderKey } });
}

async function handleRunAutofill({ extraDocs }) {
  autofillError.value = "";
  autofillCandidates.value = null;
  autofillDiagnoses.value = [];
  autofillStatus.value = AUTOFILL_STATUS.QUEUED;
  autofillInProgress.value = true;
  try {
    // The job runs server-side against an order, but until now the uploads and form state live
    // only in browser memory — so mint the draft order first. This is why clicking autofill
    // creates a draft submission.
    await savePartialOrderInfo();
    syncDraftKeyToUrl();
    const { taskId } = await orderInfoService.startCreateSubmissionAutofill(orderInfo.value.orderKey, {
      extraDocumentUrls: extraDocs.map((doc) => doc.link),
    });
    pollForAutofill(taskId, Date.now());
  } catch (error) {
    autofillInProgress.value = false;
    autofillStatus.value = AUTOFILL_STATUS.IDLE;
    autofillError.value = tryParseApiErrors(error).join(" ") || "Failed to start autofill. Please try again.";
  }
}

// One extracted value goes into one form field — from a chip click, or from applyAutofillDefaults.
// The state selects only offer full names, so an extracted "CA"/"ca" is converted to "California";
// a value matching no state is not applied at all — its chip stays visible but never turns green.
function handleApplyAutofillValue({ field, value }) {
  if (field === "patientState" || field === "providerState") {
    value = convertStateToFullName(value);
    if (!value) return;
  }
  updateOrderInfoField(field, value);
}

function isOrderInfoFieldFilled(field) {
  const value = orderInfo.value[field];
  return value !== null && value !== undefined && value !== "";
}

// Fill the form with the first candidate of every field the run found, so the user reviews filled
// values instead of clicking each one in. Only blank fields are written: anything already on the
// order was typed or picked deliberately and must not be overwritten. The review panel's green
// "applied" chip is derived from the current form value, so it lights up on its own — and the other
// candidates stay clickable where the documents offered more than one.
function applyAutofillDefaults() {
  Object.entries(autofillCandidates.value || {}).forEach(([field, candidates]) => {
    const first = Array.isArray(candidates) ? candidates[0] : null;
    if (!first || isOrderInfoFieldFilled(field)) return;
    handleApplyAutofillValue({ field, value: first.value });
  });
  // Only the primary slot is filled: the backend sorts document-flagged primaries first, while the
  // remaining diagnoses are distinct conditions rather than alternatives for the same slot.
  const firstDiagnosis = autofillDiagnoses.value[0];
  if (firstDiagnosis && !orderInfo.value.primaryDiagnosisName) {
    handleApplyAutofillDiagnosis({ slot: AUTOFILL_DIAGNOSIS_SLOT.PRIMARY, diagnosis: firstDiagnosis });
  }
}

// A diagnosis is applied as a whole pair, into whichever slot the user chose.
function handleApplyAutofillDiagnosis({ slot, diagnosis }) {
  const pair = { name: diagnosis.name, icd10Code: diagnosis.icd10Code || null };
  if (slot === AUTOFILL_DIAGNOSIS_SLOT.PRIMARY) {
    updatePrimaryDiagnosis(pair);
    return;
  }
  // Written into the first blank slot directly rather than through updateOtherDiagnosis: that
  // helper reads a missing code as "clear this row" and would splice out an already-applied
  // secondary, and the model deliberately returns diagnoses it found with no ICD-10 code.
  if (!Array.isArray(orderInfo.value.secondaryDiagnosis)) {
    orderInfo.value.secondaryDiagnosis = [];
  }
  let slotIndex = orderInfo.value.secondaryDiagnosis.findIndex((entry) => !entry?.name);
  if (slotIndex === -1) {
    addOtherDiagnosis();
    slotIndex = orderInfo.value.secondaryDiagnosis.length - 1;
  }
  orderInfo.value.secondaryDiagnosis[slotIndex] = { ...pair };
}

// Restore the order's last autofill run. The uploaded files and extracted candidates live on the
// task, not in browser state, so a reload (or reopening a draft by id) would otherwise show an
// empty uploader and lose the results while the S3 objects sat orphaned.
async function restoreAutofillRun() {
  if (!autofillEnabled.value || !orderInfo.value.orderKey) return;
  try {
    const result = await orderInfoService.getCreateSubmissionAutofill(orderInfo.value.orderKey);
    if (result.status === AUTOFILL_STATUS.NONE) return;
    autofillRestoredDocs.value = result.extraDocuments || [];
    if (result.status === AUTOFILL_STATUS.DONE) {
      autofillCandidates.value = result.result?.fields || {};
      autofillDiagnoses.value = result.result?.diagnoses || [];
      autofillStatus.value = AUTOFILL_STATUS.DONE;
      // Values live on the task, not the order, so a reload lands here with an empty form —
      // re-filling the blanks is what keeps a restored run prefilled rather than click-only.
      applyAutofillDefaults();
      return;
    }
    if (result.status === AUTOFILL_STATUS.ERROR) {
      autofillError.value = result.error || "The last autofill run failed.";
      return;
    }
    // Still queued or processing — pick the poll back up on the same task.
    autofillStatus.value = result.status;
    autofillInProgress.value = true;
    pollForAutofill(result.taskId, Date.now());
  } catch (_error) {
    // Best-effort: failing to restore a previous run must never block creating a submission
    // by hand, so this stays silent rather than surfacing an error the user can't act on.
    autofillRestoredDocs.value = [];
  }
}

function clearAutofillPoll() {
  if (autofillPollTimer) {
    clearTimeout(autofillPollTimer);
    autofillPollTimer = null;
  }
}

function pollForAutofill(taskId, startedAt, consecutiveFailures = 0) {
  clearAutofillPoll();
  autofillPollTimer = setTimeout(async () => {
    try {
      const result = await orderInfoService.getCreateSubmissionAutofill(orderInfo.value.orderKey, taskId);
      if (result.status === AUTOFILL_STATUS.DONE) {
        // Field names arrive camelized by the axios interceptor, so they already match
        // orderInfo's keys and can be applied with updateOrderInfoField directly.
        autofillCandidates.value = result.result?.fields || {};
        autofillDiagnoses.value = result.result?.diagnoses || [];
        autofillStatus.value = AUTOFILL_STATUS.DONE;
        autofillInProgress.value = false;
        applyAutofillDefaults();
        if (autofillCandidateCount.value === 0) {
          autofillError.value = "No values could be read from the uploaded documents.";
        }
        return;
      }
      if (result.status === AUTOFILL_STATUS.ERROR) {
        autofillError.value = result.error || "Autofill failed. Please fill the form in manually.";
        autofillStatus.value = AUTOFILL_STATUS.IDLE;
        autofillInProgress.value = false;
        return;
      }
      autofillStatus.value = result.status;
      if (Date.now() - startedAt > AUTOFILL_POLL_TIMEOUT_MS) {
        autofillError.value = "Autofill timed out. Please try again or fill the form in manually.";
        autofillStatus.value = AUTOFILL_STATUS.IDLE;
        autofillInProgress.value = false;
        return;
      }
      pollForAutofill(taskId, startedAt);
    } catch (error) {
      // The run itself is server-side and unaffected by a failed status check, so keep polling
      // through a blip rather than throwing away an extraction that is still going to finish.
      // Both bounds still apply: the failure budget, and the overall timeout.
      const failures = consecutiveFailures + 1;
      if (failures < AUTOFILL_POLL_MAX_CONSECUTIVE_FAILURES && Date.now() - startedAt <= AUTOFILL_POLL_TIMEOUT_MS) {
        // eslint-disable-next-line no-console -- a swallowed retry needs some trace to debug from
        console.warn(`Autofill status check failed (${failures}), retrying`, error);
        pollForAutofill(taskId, startedAt, failures);
        return;
      }
      autofillError.value = tryParseApiErrors(error).join(" ") || "Failed to check autofill progress.";
      autofillStatus.value = AUTOFILL_STATUS.IDLE;
      autofillInProgress.value = false;
    }
  }, AUTOFILL_POLL_INTERVAL_MS);
}

onUnmounted(clearAutofillPoll);

// Which patient's source last prepopulated this form. Distinguishes a patient switch (must clear
// the user-entered medication queues — leaving them would file the previous patient's drugs under
// the new one) from a same-patient re-prepopulate (must keep them — no prepopulate source ever
// supplies extraMedications/additionalMedications, so a wipe here is unrecoverable).
const lastPrepopulatedPatientId = ref(null);

function handlePrepopulateOrderInfo(prepopulatedData, sourcePatientId) {
  const samePatient = sourcePatientId != null && sourcePatientId === lastPrepopulatedPatientId.value;
  lastPrepopulatedPatientId.value = sourcePatientId ?? null;
  // List of all fields that can be prepopulated from intake data
  // Clear these fields first to prevent old values from persisting when selection changes
  const prepopulatableFields = [
    // Patient fields
    "patientFirstName",
    "patientLastName",
    "patientDob",
    "patientExternalId",
    // Insurance fields
    "patientMemberId",
    "insuranceProvider",
    "insurancePlanType",
    "benefitType",
    "bin",
    "processorControlNumber",
    "grpNumber",
    // Medication fields
    "medication",
    "dailyDosage",
    "nationalDrugCode",
    "prescriptionNumber",
    "frequency",
    "expectedLengthOfTherapy",
    "quantityPer30Days",
    "daw",
    "routeAdministration",
    // Both medication queues belong to the PATIENT being switched away from: extraMedications are
    // extra drugs on this one submission's fax form, additionalMedications each become their own
    // PA at submit. Left populated, switching patient carries the previous patient's drugs onto the
    // new one — and additionalMedications would batch-submit them under that new patient.
    // initializeOrderInfo() already empties both on a fresh visit; a patient switch is the same
    // reset. But cleared ONLY on an actual patient switch (see medicationQueueFields below):
    // no prepopulate source supplies either queue, so on a same-patient re-prepopulate a clear
    // would silently destroy user-entered drugs with nothing to restore them.
    "extraMedications",
    "additionalMedications",
    // Provider fields
    "providerFirstName",
    "providerLastName",
    "providerTitle",
    "providerNpiNumber",
    "providerContactFax",
    "providerContactPhone",
    "providerAddress1",
    "providerCity",
    "providerState",
    "providerZip",
    "providerOfficeName",
    "providerSpecialty",
    // Patient demographics from order form
    "patientGender",
    "patientAddress1",
    "patientCity",
    "patientState",
    "patientZip",
    "patientWeight",
    "patientHeight",
    "patientDrugAllergies",
    "stageOfTherapy",
    // Diagnosis fields
    "primaryDiagnosisIcd10Code",
    "primaryDiagnosisName",
    // secondaryDiagnosis intentionally NOT cleared here — DiagnosisSelectionPreviewComponent
    // renders it directly as an array; clearing to null (this loop's generic default) would
    // break that render. Matches existing precedent — this field was never in the clear list.
    // Treatment
    "treatmentStartDate",
    // patientChart - replace, don't merge when new selection is made
    "patientChart",
    // Additional patient chart MRNs (multi-patient chart support)
    "additionalPatientExternalIds",
    // Fax-form structured data (Brooks import) — no dedicated form input, shown read-only
    // before prepopulation, but must still clear when a different source is selected.
    "extraStructuredInformation",
    // Fax destination fields resolved from the matched FaxSubmissionFormDetail (LAM-4720) — only
    // present in prepopulatedData when the newly selected row resolves to FAX; must still clear
    // here so a stale fax number/description from a PRIOR FAX-routed selection doesn't survive
    // switching to a row that resolves to CoverMyMeds instead.
    "sendFaxFormTo",
    "faxDescription",
    // Explicit fax-form selection (LAM-5197) — company-level, but a selection made for the prior
    // source must not silently ride into the newly prepopulated one.
    "faxSubmissionFormDetail",
  ];

  // Array-valued fields reset to [], never null — every consumer reads .length or v-for's them,
  // so the generic null default below would break the render.
  const arrayValuedFields = [
    "patientChart",
    "additionalPatientExternalIds",
    "extraMedications",
    "additionalMedications",
  ];

  // User-entered medication queues: cleared only on an actual patient switch. Everything else in
  // the clear list is either restored from the new payload or belongs to the outgoing source.
  const medicationQueueFields = ["extraMedications", "additionalMedications"];

  // Cleared to "" rather than null. On an already-persisted draft (autofill and Save Draft both
  // mint the order) a null never clears server-side: save-order's _set_fields skips None, so the
  // previous patient's value would silently survive on the row while the form shows blank. ""
  // passes the backend's `is not None` check and actually clears it. Only these two need it —
  // no prepopulate source reliably supplies them, so "" is what gets persisted at submit.
  const stringClearedFields = ["patientExternalId", "prescriptionNumber"];

  // Clear all prepopulatable fields first to ensure old values don't persist
  prepopulatableFields.forEach((field) => {
    if (samePatient && medicationQueueFields.includes(field)) {
      return;
    }
    if (arrayValuedFields.includes(field)) {
      updateOrderInfoField(field, []);
    } else if (stringClearedFields.includes(field)) {
      updateOrderInfoField(field, "");
    } else {
      updateOrderInfoField(field, null);
    }
  });

  // Now apply the new prepopulated data
  Object.keys(prepopulatedData).forEach((field) => {
    if (prepopulatedData[field] !== null && prepopulatedData[field] !== undefined) {
      // Special handling for patientChart - replace with new data (already cleared above)
      if (field === "patientChart" && Array.isArray(prepopulatedData[field])) {
        updateOrderInfoField(field, prepopulatedData[field]);
      } else {
        updateOrderInfoField(field, prepopulatedData[field]);
      }
    }
  });

  // SearchPatientByExternalIdView (search + source selection) renders above this entire form —
  // after prepopulating, the viewport doesn't move on its own, so the user is left looking at
  // whatever was directly under the button they just clicked (e.g. the imported-order card's
  // read-only info) instead of the form fields they now need to review. Bring the form into view
  // explicitly, same pattern already used for scroll-to-invalid-field below.
  nextTick(() => {
    formHeadingRef.value?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  });
}

onBeforeMount(async () => {
  clearOrderInfoStorage();
  // Needed on both paths now: the autofill card is gated on a per-tenant config flag, so the
  // config has to be loaded even when opening an existing draft by id.
  if (!requirementsStore.clientConfig) {
    try {
      await requirementsStore.refreshCurrentUserClientConfig();
    } catch (err) {
      // Which controls this page shows is config-driven, and the store nulls the cached config
      // before it fetches — so rendering the form anyway would show the wrong page. Fail the
      // whole page instead of loading a half-configured form.
      pageLoadFailed.value = true;
      errors.value = ["Failed to load your account settings. Please reload the page and try again."];
      // The pageLoadFailed alert sits behind the skeleton (v-else-if), so drop the preloader.
      preloader.value = false;
      return;
    }
  }
  if (route.params.id) {
    loadOrder(route.params.id);
  } else {
    initializeOrderInfo();
    applyPriorAuthDefaults();
  }
  supplementalDocs.value.push({ label: null, links: [] });

  // Non-blocking and non-fatal (unlike the client config above): with no mapped forms — or a
  // failed fetch — the fax panel simply doesn't render and today's routing applies unchanged.
  mainServices
    .getFaxForms()
    .then((forms) => {
      faxForms.value = Array.isArray(forms) ? forms : [];
    })
    .catch(() => {
      faxForms.value = [];
    });

  formButtonClicked.value = !!route.params.id;
  expandedSections.value = [
    "patient-info",
    "provider-info",
    "medical-info",
    "diagnosis-info",
    "supporting-docs",
    "expedited-review",
    "fax-form",
  ];
});

function initializeOrderInfo() {
  orderInfo.value = {
    ...orderInfo.value,
    formStep: "Review",
    submissionMethod: "CoverMyMeds",
    // A leftover orderSubmission from a previously viewed CMM-imported order flips isCmmImport,
    // hiding the insurance fields and exempting them from validation — never inherit one.
    orderSubmission: null,
    extraMedications: [],
    // Without this, additional medications queued (but not submitted, or just submitted) in a
    // prior session would silently carry into the next "Create Another Submission"/fresh visit
    // and get re-submitted for whatever patient the user enters next.
    additionalMedications: [],
    secondaryDiagnosis: [{ icd10Code: null, name: null }],
    patientChart: [],
  };
}

function applyPriorAuthDefaults() {
  const defaults = requirementsStore.clientConfig?.priorAuth?.defaults;
  if (!defaults || typeof defaults !== "object") return;
  Object.entries(defaults).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    updateOrderInfoField(key, value);
  });
}

async function loadOrder(orderKey) {
  preloader.value = true;
  try {
    errors.value = [];
    const fetchedOrder = await orderInfoService.getOrderDetails(orderKey);
    orderInfo.value = fetchedOrder.orderInfo;
    orderInfo.value.formStep = "Review";
    // additionalMedications is frontend-only staging state (LAM-4841) — the backend has no such
    // field, so a fetched order never carries one; always start the queue empty on (re)load.
    orderInfo.value.additionalMedications = [];
    // Same for the fax-form selection (LAM-5197): it is consumed at create time, never persisted
    // on a draft, so a reopened draft always starts unselected.
    orderInfo.value.faxSubmissionFormDetail = null;
    // Preserve the real persisted submission method (e.g. a fax order created from imported
    // data) — only fall back to the default when the fetched order genuinely has none yet.
    // (Previously this unconditionally overwrote it with "CoverMyMeds", clobbering fax orders.)
    orderInfo.value.submissionMethod = fetchedOrder.orderInfo.submissionMethod || "CoverMyMeds";
    await restoreAutofillRun();
  } catch (err) {
    errors.value = ["Failed to load the order. Please try again later or start a new submission."];
    initializeOrderInfo();
  }
  preloader.value = false;
}

async function handleSaveDraft() {
  draftSavingInProgress.value = true;
  try {
    errors.value = [];
    await savePartialOrderInfo();
    syncDraftKeyToUrl();
  } catch (error) {
    errors.value = ["Failed to save draft. Please try again."];
  }
  draftSavingInProgress.value = false;
}

function normalizeMedicationName(name) {
  return (name || "").trim().toLowerCase();
}

async function submitAdditionalMedications() {
  const sharedFields = { ...orderInfo.value };
  delete sharedFields.additionalMedications;
  delete sharedFields.orderKey;

  // Duplicate-of-primary check happens client-side because the primary medication is never
  // part of the batch request (it's already its own submission via createEmptyOrder/saveOrder
  // above) — the backend's own dedupe only sees additionalMedications against each other.
  const primaryKey = normalizeMedicationName(orderInfo.value.medication);
  const duplicatesOfPrimary = [];
  const toSubmit = [];
  orderInfo.value.additionalMedications.forEach((medication) => {
    if (normalizeMedicationName(medication.medication) === primaryKey) {
      duplicatesOfPrimary.push({ medication: medication.medication, success: false, skippedDuplicate: true });
    } else {
      toSubmit.push(medication);
    }
  });

  const primaryResult = { medication: orderInfo.value.medication, success: true, orderKey: orderInfo.value.orderKey };
  if (toSubmit.length === 0) {
    return [primaryResult, ...duplicatesOfPrimary];
  }
  const { results } = await orderInfoService.createSubmissionsBatch(sharedFields, toSubmit);
  return [primaryResult, ...duplicatesOfPrimary, ...results];
}

async function handleCreateSubmission() {
  formButtonClicked.value = true;
  if (supplementalDocs.value.some((doc) => doc.links?.length > 0)) {
    mergeSupplementalDocsWithPatientChart();
  }
  errors.value = [];
  batchSubmissionResults.value = null;
  if (!validPatientInfo()) {
    expandedSections.value.push("patient-info");
    errors.value.push("Please fill in all required patient information fields.");
  }
  if (!validProviderInfo()) {
    expandedSections.value.push("provider-info");
    errors.value.push("Please fill in all required provider information fields.");
  }
  if (!validMedicationInfo() || !validAdditionalMedicationsInfo()) {
    expandedSections.value.push("medical-info");
    errors.value.push("Please fill in all required medication information fields.");
  }
  if (!validDiagnosisInfo()) {
    expandedSections.value.push("diagnosis-info");
    errors.value.push("Please fill in all required diagnosis information fields.");
  }
  if (!validSupportingDocs()) {
    expandedSections.value.push("supporting-docs");
    errors.value.push("Please add at least one supporting document.");
  }
  if (faxFormOptedIn.value && !orderInfo.value.faxSubmissionFormDetail) {
    expandedSections.value.push("fax-form");
    errors.value.push("Please select a fax form, or uncheck the fax submission option.");
  }
  await new Promise((resolve) => setTimeout(resolve, 300));
  const errorClassNames = ["v-input--error", "form-validation-alert"];
  let missingInput = [];
  errorClassNames.forEach((className) => {
    const foundElements = document.getElementsByClassName(className);
    missingInput = [...missingInput, ...foundElements];
  });
  if (errors.value.length === 0 && missingInput.length === 0) {
    draftSavingInProgress.value = true;
    try {
      if (!orderInfo.value.orderKey) {
        const newOrder = await orderInfoService.createEmptyOrder(orderInfo.value);
        orderInfo.value.orderKey = newOrder.orderKey;
      }
      orderInfo.value.formStep = "Create Submission";
      await orderInfoService.saveOrder(orderInfo.value.orderKey, orderInfo.value);
    } catch (err) {
      errors.value = tryParseApiErrors(err);
      draftSavingInProgress.value = false;
      return;
    }
    // The primary is committed from here on — a batch failure (e.g. a 404 mid rolling-deploy,
    // or a network blip) must not be reported as if nothing was submitted, since something was.
    if (orderInfo.value.additionalMedications.length > 0) {
      try {
        batchSubmissionResults.value = await submitAdditionalMedications();
      } catch (err) {
        batchSubmissionResults.value = [
          { medication: orderInfo.value.medication, success: true, orderKey: orderInfo.value.orderKey },
          ...orderInfo.value.additionalMedications.map((medication) => ({
            medication: medication.medication,
            success: false,
            error: "Could not be submitted — please add it again from the Submissions list.",
          })),
        ];
      }
    }
    errors.value = [];
    expandedSections.value = [];
    showCreateSuccessDialog.value = true;
    draftSavingInProgress.value = false;
  } else if (missingInput.length > 0) {
    setTimeout(() => {
      const parentDiv = missingInput[0].parentNode;
      window.scrollTo({
        top: parentDiv.offsetTop,
        behavior: "smooth",
      });
    }, 0);
  }
}

function onSuccessReturnToAll() {
  showCreateSuccessDialog.value = false;
  batchSubmissionResults.value = null;
  clearOrderInfoStorage();
  router.push({ name: "all-submissions" });
  window.scrollTo(0, 0);
}

// Deliberately a full browser reload rather than an in-place reset or a router navigation.
//
// This page accumulates state in three places the parent can't reach in one sweep: the shared
// orderInfoStore, this component's own refs (autofill candidates, poll timer, errors), and local
// state inside children — the autofill card's extra-file uploads are private to it by design, so
// they never reach orderInfo.patientChart. Resetting that by hand means a list that has to grow
// every time page state is added, and a missed entry leaks the previous patient's data into the
// next submission. A reload starts from a guaranteed-empty page instead.
//
// A router navigation would not do it: this route is /create-submission/:id?, and Vue Router
// reuses the component across a param change — which syncDraftKeyToUrl() above relies on — so
// replacing the route drops the id without remounting anything.
//
// Safe to reload here: the submission is already committed server-side, the auth session lives in
// localStorage, and orderInfoStore is in-memory Pinia with no persistence, so nothing needs
// clearing first. Cost is a bundle re-fetch and a page flash on each "create another".
function onSuccessCreateAnother() {
  showCreateSuccessDialog.value = false;
  // resolve() rather than a literal path so the app's router base is honoured.
  window.location.assign(router.resolve({ name: "create-submission" }).href);
}
</script>

<style lang="scss" scoped>
.detail-page__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #5e7898;
  cursor: pointer;
  padding: 4px 0;
  margin-bottom: 8px;
  transition: color 150ms;

  &:hover {
    color: #052e5e;
  }
}
</style>
